import type { Signal } from '@angular/core';
import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { type Subscription, filter } from 'rxjs';

import { AppConfigService } from '../config/app-config.service';
import { AuthStore } from './auth.store';

/** Minutos de inactividad por defecto antes del logout (config: `session.idleTimeoutMinutes`). */
const DEFAULT_IDLE_MINUTES = 15;
/** Segundos de aviso previo por defecto (config: `session.idleWarningSeconds`). */
const DEFAULT_WARNING_SECONDS = 60;

/**
 * Cota superior del timeout (24 h). Los delays de `setTimeout` son int32: uno
 * mayor a ~2^31-1 ms (~24,8 días) overflowa y dispara casi de inmediato. Una
 * config absurda (miles de millones de minutos) NO debe volver el control más
 * agresivo de lo esperado, así que el valor se acota acá (ver resolveConfig).
 */
const MAX_IDLE_MINUTES = 1440;

/**
 * Los eventos de actividad de alta frecuencia (pointermove/scroll) no reinician
 * el timer más de una vez por ventana: clearTimeout+setTimeout en cada mousemove
 * es innecesario. El reinicio real ocurre a lo sumo cada este intervalo.
 */
const ACTIVITY_THROTTLE_MS = 1000;

/**
 * Señales de actividad del usuario que reinician el timer de inactividad
 * (mouse/pen, teclado, touch, scroll). La navegación se cubre aparte, vía
 * `Router` (ver start()). Se escuchan en captura sobre `window` para atrapar
 * también los eventos que no burbujean (scroll) y los detenidos con
 * stopPropagation.
 */
const ACTIVITY_EVENTS = [
  'pointerdown',
  'pointermove',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
] as const;

/**
 * Auto-logout por inactividad (LION-794).
 *
 * Mientras hay sesión, monitorea la actividad del usuario. Tras
 * `session.idleTimeoutMinutes` sin interacción muestra un aviso previo
 * (`warningActive` + `remainingSeconds` para la UI) durante
 * `session.idleWarningSeconds` y, si nadie confirma presencia, dispara el
 * **logout real de {@link AuthStore.logout}** — que revoca la sesión
 * server-side (`POST /auth/logout`). NO duplica la lógica de logout.
 *
 * Ciclo de vida EXPLÍCITO: el shell autenticado llama `start()` al montar y
 * `stop()` al destruirse (logout / cierre del área). El timer SOLO corre entre
 * ambos; `stop()` remueve listeners y limpia timers (sin fugas). El servicio es
 * `providedIn: 'root'` pero inerte hasta el `start()`.
 *
 * Reloj de pared (defensa contra tabs en background): los browsers throttlean
 * los `setTimeout`/`setInterval` de una pestaña oculta (hasta ~1/min o ~1/hora),
 * así que el logout se retrasaría MUCHO justo en el escenario que el control
 * cubre. Por eso la fuente de verdad es un timestamp `Date.now()` de la última
 * actividad, y ante `visibilitychange` (o el disparo tardío del propio timer) se
 * RECONCILIA con el tiempo real transcurrido: si ya venció, logout inmediato; si
 * está dentro del aviso, lo muestra con la cuenta regresiva correcta; si no,
 * reprograma con lo que realmente queda.
 *
 * Decisión de UX/seguridad: durante el aviso, la actividad PASIVA
 * (mouse/scroll) NO reinicia el ciclo — el usuario debe confirmar
 * explícitamente con "seguir conectado" ({@link continueSession}). Así el aviso
 * es una confirmación deliberada de presencia, no algo que un mousemove casual
 * cancela.
 */
@Injectable({ providedIn: 'root' })
export class IdleTimeoutService {
  private readonly appConfig = inject(AppConfigService);
  private readonly auth = inject(AuthStore);
  private readonly router = inject(Router);

  private readonly _warningActive = signal(false);
  private readonly _remainingSeconds = signal(0);

  /** True mientras el aviso previo está visible (el shell renderiza el modal). */
  readonly warningActive: Signal<boolean> = this._warningActive.asReadonly();
  /** Segundos restantes del aviso antes del logout (cuenta regresiva para la UI). */
  readonly remainingSeconds: Signal<number> = this._remainingSeconds.asReadonly();

  private idleMs = DEFAULT_IDLE_MINUTES * 60_000;
  private warningMs = DEFAULT_WARNING_SECONDS * 1000;

  private preWarningTimer: ReturnType<typeof setTimeout> | undefined;
  private logoutTimer: ReturnType<typeof setTimeout> | undefined;
  private countdownTimer: ReturnType<typeof setInterval> | undefined;
  private routerSub: Subscription | undefined;
  /** Epoch ms de la última actividad. Fuente de verdad (los timers solo aproximan). */
  private lastActivityAt = 0;
  private active = false;

  constructor() {
    // Red de contención: si el injector raíz se destruye (teardown de la app)
    // sin un stop() previo, no dejamos listeners/timers colgados.
    inject(DestroyRef).onDestroy(() => this.stop());
  }

  /**
   * Arranca el monitoreo (idempotente). Lee el timeout de la config de runtime,
   * registra los listeners de actividad + navegación + visibilidad y agenda el ciclo.
   */
  start(): void {
    if (this.active) return;
    this.resolveConfig();
    this.active = true;
    this.lastActivityAt = Date.now();
    for (const type of ACTIVITY_EVENTS) {
      window.addEventListener(type, this.onActivity, { passive: true, capture: true });
    }
    // Reconciliación por reloj de pared al volver el tab a primer plano (ver
    // docstring): los timers de un tab oculto llegan tarde por el throttling.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
    // La navegación (SPA) cuenta como actividad aunque no haya un evento DOM
    // asociado (p. ej. redirects programáticos por interacción).
    this.routerSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.registerActivity());
    this.scheduleFromActivity();
  }

  /** Detiene el monitoreo: remueve listeners, limpia timers y oculta el aviso. */
  stop(): void {
    if (!this.active) return;
    this.active = false;
    for (const type of ACTIVITY_EVENTS) {
      window.removeEventListener(type, this.onActivity, { capture: true });
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    this.routerSub?.unsubscribe();
    this.routerSub = undefined;
    this.clearTimers();
    this._warningActive.set(false);
    this._remainingSeconds.set(0);
  }

  /**
   * "Seguir conectado": el usuario confirmó presencia desde el aviso. Oculta el
   * aviso y reinicia el ciclo completo de inactividad.
   */
  continueSession(): void {
    if (!this.active) return;
    this._warningActive.set(false);
    this._remainingSeconds.set(0);
    this.lastActivityAt = Date.now();
    this.scheduleFromActivity();
  }

  /** Logout inmediato desde el aviso (mismo camino real que la expiración). */
  logoutNow(): void {
    this.expire();
  }

  /** Lee el timeout/aviso de la config, con defaults, cota superior y recorte del aviso. */
  private resolveConfig(): void {
    const session = this.appConfig.config().session;
    const minutes = this.clampPositive(
      session?.idleTimeoutMinutes,
      DEFAULT_IDLE_MINUTES,
      MAX_IDLE_MINUTES,
    );
    this.idleMs = minutes * 60_000;
    const rawWarningMs =
      this.clampPositive(session?.idleWarningSeconds, DEFAULT_WARNING_SECONDS, MAX_IDLE_MINUTES * 60) *
      1000;
    // El aviso nunca puede igualar/superar el total: se acota a la mitad del
    // ciclo (y a >=1s) para que SIEMPRE exista una fase previa de inactividad.
    this.warningMs = Math.max(1000, Math.min(rawWarningMs, Math.floor(this.idleMs / 2)));
  }

  /**
   * Normaliza un valor de config: usa `fallback` si falta / no es número finito /
   * es <= 0, y lo acota a `max`. La cota superior evita el overflow del delay
   * int32 de `setTimeout` ante una config absurda (efecto: se acota, no dispara).
   */
  private clampPositive(value: number | undefined, fallback: number, max: number): number {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return fallback;
    return Math.min(value, max);
  }

  private readonly onActivity = (): void => {
    this.registerActivity();
  };

  /** Registra actividad (throttled). Ignorada durante el aviso (ver decisión de UX). */
  private registerActivity(): void {
    // Tras stop() un evento tardío no debe re-armar el ciclo (defensa extra: los
    // listeners ya fueron removidos, pero la navegación podría emitir en la cola).
    if (!this.active || this._warningActive()) return;
    const now = Date.now();
    if (now - this.lastActivityAt < ACTIVITY_THROTTLE_MS) return;
    this.lastActivityAt = now;
    this.scheduleFromActivity();
  }

  private readonly onVisibilityChange = (): void => {
    if (!this.active) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    this.reconcile();
  };

  /**
   * Reconcilia el ciclo con el tiempo REAL transcurrido desde la última actividad
   * (reloj de pared, no el timer que pudo throttlearse en background).
   */
  private reconcile(): void {
    const elapsed = Date.now() - this.lastActivityAt;
    if (elapsed >= this.idleMs) {
      // Inactivo más que el timeout mientras el tab estaba oculto: logout YA.
      this.expire();
      return;
    }
    if (elapsed >= this.idleMs - this.warningMs) {
      // Dentro de la ventana de aviso: mostrarlo con la cuenta regresiva correcta.
      this.enterWarning(this.idleMs - elapsed);
      return;
    }
    // Todavía en fase previa: reprogramar con el tiempo que realmente queda.
    this.scheduleFromActivity();
  }

  /** (Re)agenda la transición a la fase de aviso, anclada al reloj de pared. */
  private scheduleFromActivity(): void {
    this.clearTimers();
    const untilWarning = this.idleMs - this.warningMs - (Date.now() - this.lastActivityAt);
    this.preWarningTimer = setTimeout(() => this.onPreWarningElapsed(), Math.max(0, untilWarning));
  }

  /**
   * Disparo del timer de fin de la fase previa. Recalcula con reloj de pared por
   * si llegó TARDE (tab en background): si ya venció, logout; si no, entra al
   * aviso con el remanente real.
   */
  private onPreWarningElapsed(): void {
    const remainingMs = this.idleMs - (Date.now() - this.lastActivityAt);
    if (remainingMs <= 0) {
      this.expire();
      return;
    }
    this.enterWarning(remainingMs);
  }

  /** Entra en la fase de aviso: muestra el modal, arranca la cuenta regresiva y el logout. */
  private enterWarning(remainingMs: number): void {
    this.clearTimers();
    this._warningActive.set(true);
    this._remainingSeconds.set(Math.max(1, Math.ceil(remainingMs / 1000)));
    this.countdownTimer = setInterval(() => {
      this._remainingSeconds.update((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    this.logoutTimer = setTimeout(() => this.expire(), remainingMs);
  }

  /** Vencimiento: corta el monitoreo y dispara el logout real (revoca server-side). */
  private expire(): void {
    // stop() ANTES del logout: la navegación al login que dispara logout() no
    // debe re-registrarse como actividad ni dejar timers vivos.
    this.stop();
    this.auth.logout();
  }

  private clearTimers(): void {
    if (this.preWarningTimer) clearTimeout(this.preWarningTimer);
    if (this.logoutTimer) clearTimeout(this.logoutTimer);
    this.preWarningTimer = undefined;
    this.logoutTimer = undefined;
    this.clearCountdown();
  }

  private clearCountdown(): void {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    this.countdownTimer = undefined;
  }
}
